// @ts-strict-ignore
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import isString from 'lodash/isString';
import pickBy from 'lodash/pickBy';
import isUndefined from 'lodash/isUndefined';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';

import {insightsFilterToPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {DATASET_MIXPANEL, PropertyType} from 'common/report/constants';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Bucket, StringBucket} from 'common/widgets/bucket-pane/types';
import {ProfileHistoryOptions} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FilterValue} from 'common/widgets/property-filter-menu/models/types';
import {GroupByCohort} from 'common/report/insights/util/types';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {GroupClause as GroupClauseType, InsightsResourceType} from 'common/types/reports/bookmark';

import {INSIGHTS_GROUP_PROPERTIES, TIME_UNIT_GROUPING_LIST, TIME_UNITS} from './constants';
import {sanitizeIfCustomProperty} from './utils';
import {ClauseType} from './types';

const CREATE_CUSTOM_PROPERTY = {
  icon: `compose`,
  name: `$custom_property`,
  type: `custom`,
  special: true,
  hasPill: false,
};

/** @deprecated Use POJO. For typing use iron/common/types/reports/bookmark.ts GroupClause. */
export class GroupClause {
  static TYPE: typeof ClauseType.Group = ClauseType.Group;
  static EVENT_DATE = {
    label: `Date`,
    icon: `star-top-events`,
    name: `$date`,
    type: `datetime`,
    resourceType: `events`,
    special: true,
  };

  static CREATE_CUSTOM_EVENT_PROPERTY = Object.assign(
    {label: `Create event property...`, resourceType: `events`},
    CREATE_CUSTOM_PROPERTY,
  );
  static CREATE_CUSTOM_PEOPLE_PROPERTY = Object.assign(
    {label: `Create people property...`, resourceType: `people`},
    CREATE_CUSTOM_PROPERTY,
  );
  static PropertyType = Object.values(PropertyType);
  static PROPERTY_TYPECASTS = [`string`, `number`, `boolean`];

  declare behavior?: GroupClauseType[`behavior`];
  declare customBucket?: GroupClauseType[`customBucket`];
  declare customGroups?: GroupClauseType[`customGroups`];
  declare customProperty?: GroupClauseType[`customProperty`];
  declare customPropertyId?: GroupClauseType[`customPropertyId`];
  declare dataGroupId?: GroupClauseType[`dataGroupId`];
  declare dataset: GroupClauseType[`dataset`];
  declare datasetId?: GroupClauseType[`datasetId`];
  declare editing: GroupClauseType[`editing`] | null | undefined;
  declare joinPropertyType?: GroupClauseType[`joinPropertyType`];
  declare listItemGroup?: GroupClauseType[`listItemGroup`];
  declare listQuantifier?: GroupClauseType[`listQuantifier`];
  declare profileType?: GroupClauseType[`profileType`];
  declare projectId?: GroupClauseType[`projectId`];
  declare propertyObjectKey?: GroupClauseType[`propertyObjectKey`];
  declare propertyType?: GroupClauseType[`propertyType`];
  declare resourceType: GroupClauseType[`resourceType`];
  declare search?: GroupClauseType[`search`];
  declare tempDataGroupId?: GroupClauseType[`tempDataGroupId`];
  declare typeCast?: GroupClauseType[`typeCast`];
  declare unit?: GroupClauseType[`unit`];
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  declare value: GroupClauseType[`value`] | null;

  // Fields not included on GroupClauseType
  declare cohorts?: GroupByCohort[] | null;
  declare filterOperator: TODO;
  declare filterDateUnit: TimeUnit;
  declare filterJoinType: TODO;
  declare filterType: TODO;
  declare filterValue: FilterValue | null;
  declare propertyFilter: PropertyFilter;
  declare profileHistoryOptions?: ProfileHistoryOptions | null;

  declare scdEvent?: GroupClauseType[`scdEvent`];
  declare scdOptions?: GroupClauseType[`scdOptions`];
  declare experiments?: GroupClauseType[`experiments`];

  // for funnel step ttc step selection
  declare startStepNumber?: GroupClauseType[`startStepNumber`];
  declare endStepNumber?: GroupClauseType[`endStepNumber`];

  declare isHidden?: GroupClauseType[`isHidden`];
  declare isInherited?: GroupClauseType[`isInherited`];

  private clauseConstructor({dataset = undefined, behavior = undefined}: {dataset?: string; behavior?: any}) {
    dataset = behavior?.dataset || dataset;
    this.dataset = dataset === `mixpanel` ? DATASET_MIXPANEL : dataset;
  }

  private eventsConstructor(attrs: any) {
    this.clauseConstructor(attrs);
    this.value = attrs.value || null;
    this.resourceType = attrs.resourceType || InsightsResourceType.Events;
    this.profileType = attrs.profileType || null;
    this.search = attrs.search || ``;
    this.dataGroupId = attrs.dataGroupId || null;
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }

    Object.assign(this, sanitizeIfCustomProperty(pick(attrs, `customPropertyId`, `customProperty`)));
  }

  constructor(attrs: Partial<GroupClauseType> = {}) {
    this.eventsConstructor(attrs);
    if (attrs.joinPropertyType) {
      this.joinPropertyType = attrs.joinPropertyType;
    }
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }
    this.propertyType = attrs.propertyType || null;
    this.typeCast = attrs.typeCast || null;
    this.propertyObjectKey = attrs.propertyObjectKey;
    this.unit = attrs.unit || null;
    this.editing = attrs.editing || null;
    if (this.isDatetimeProperty && !this.editing && this.unit === undefined) {
      this.unit = TIME_UNITS.DAY;
    }
    if (attrs.customBucket) {
      this.customBucket = attrs.customBucket;
    }
    if (attrs.customGroups) {
      this.customGroups = attrs.customGroups;
    }
    if (attrs.behavior) {
      this.behavior = {
        ...attrs.behavior,
        // Cleanup: Either Behavior doesn't have custom properties or it needs to be added to the type.
        property: attrs.behavior.property && sanitizeIfCustomProperty(attrs.behavior.property),
      };
    }
    if (attrs.cohorts) {
      this.cohorts = attrs.cohorts;
    }
    if (attrs.scdEvent) {
      this.scdEvent = attrs.scdEvent;
    }

    if (attrs.scdOptions) {
      this.scdOptions = attrs.scdOptions;
    }

    if (attrs.experiments) {
      this.experiments = attrs.experiments;
    }

    if (attrs.isHidden) {
      this.isHidden = attrs.isHidden;
    }

    if (attrs.isInherited) {
      this.isInherited = attrs.isInherited;
    }

    //To determine whether it is a filter or not
    this.filterOperator = null;

    // Support for Single Segment in GroupClause (reassigns filterOperator here if necessary)
    for (const attrName of Object.keys(attrs)) {
      //Add all filter properties to the GroupClause if they exist
      if (!INSIGHTS_GROUP_PROPERTIES.includes(attrName)) {
        this[attrName] = attrs[attrName];
      }
    }

    //Make sure unit and filterDateUnit are equal if filterDateUnit exists
    if (this.filterDateUnit) {
      this.unit = this.filterDateUnit;
    }

    if (attrs.listQuantifier) {
      this.listQuantifier = attrs.listQuantifier;
    }
    if (attrs.listItemGroup) {
      this.listItemGroup = attrs.listItemGroup;
    }

    if (attrs.profileHistoryOptions) {
      this.profileHistoryOptions = attrs.profileHistoryOptions;
    }

    // propertyFilter is strictly used for rendering purposes since it contains the unflattened version of filterValue and the generic (non Insights specific) DateRangeType filterOperator]
    // also used to extract `filterValue` for boolean properties for Insights.
    this.propertyFilter = insightsFilterToPropertyFilter(this);

    if (isNumber(attrs.startStepNumber) || attrs.startStepNumber === null) {
      this.startStepNumber = attrs.startStepNumber;
    }
    if (isNumber(attrs.endStepNumber) || attrs.endStepNumber === null) {
      this.endStepNumber = attrs.endStepNumber;
    }
  }

  extend(attrs = {} as Partial<GroupClause[`attrs`]>) {
    return new GroupClause({...this.attrs, ...attrs});
  }

  get isCustomProperty() {
    return !!this.customProperty || !!this.customPropertyId;
  }

  isCustomPropertyValid() {
    return (
      this.customPropertyId ||
      (this.customProperty.name && this.customProperty.composedProperties && this.customProperty.displayFormula)
    );
  }

  get valid() {
    const eventValid =
      Object.values(InsightsResourceType).includes(this.resourceType) &&
      isString(this.search) &&
      (!this.isCustomProperty || this.isCustomPropertyValid());
    if (this.isGroupByCohorts()) {
      return this.isGroupByCohortsValid();
    }
    return eventValid && (this.isCustomProperty || this.isRegularPropertyValid());
  }

  isRegularPropertyValid() {
    const validIfDatetime = this.isDatetimeProperty ? !this.unit || TIME_UNIT_GROUPING_LIST.includes(this.unit) : true;

    return Object.values(GroupClause.PropertyType).includes(this.propertyType) && validIfDatetime;
  }

  isGroupByCohorts() {
    return !!this.cohorts && this.cohorts?.length > 0;
  }

  isGroupByCohortsValid() {
    const cohorts = this.cohorts || [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return cohorts.every(({negated, raw_cohort, name, id}) => !!(isBoolean(negated) && (raw_cohort || (id && name))));
  }

  private clauseAttrs() {
    return pick(this, [`dataset`]);
  }

  private eventAttributes() {
    return {
      ...this.clauseAttrs(),
      ...pick(this, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `search`,
        `dataGroupId`,
        `tempDataGroupId`,
        `scdEvent`,
        `scdOptions`,
      ]),
    };
  }

  private cohortAttributes(cohort: GroupByCohort): GroupByCohort {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {id, name, negated, raw_cohort} = cohort;
    return pickBy(
      {
        id,
        name,
        negated,
        raw_cohort,
      },
      (value) => !isUndefined(value),
    );
  }

  get attrs() {
    const {
      cohorts,
      joinPropertyType,
      scdEvent,
      propertyObjectKey,
      tempDataGroupId,
      propertyType,
      typeCast,
      unit,
      customBucket,
      customGroups,
      filterOperator,
      filterValue,
      filterType,
      listQuantifier,
      listItemGroup,
      filterJoinType,
      profileHistoryOptions,
      startStepNumber,
      endStepNumber,
      isHidden,
      isInherited,
      experiments,
    } = this;
    const isFilter = !isNil(this.filterOperator);
    const type = listItemGroup?.typeCast || listItemGroup?.propertyType || typeCast || propertyType;
    return {
      ...this.eventAttributes(),
      ...(cohorts ? {cohorts} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(scdEvent ? {scdEvent} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      propertyType,
      ...(isFilter ? {} : {typeCast}),
      ...(isFilter ? {defaultType: propertyType} : {}),
      ...(isFilter ? {filterDateUnit: unit} : {unit}),
      ...(customBucket && type === PropertyType.Number ? {customBucket} : {}),
      ...(customGroups && [PropertyType.String, PropertyType.List].includes(type) ? {customGroups} : {}),
      ...(filterOperator ? {filterOperator} : {}),
      ...(!isNil(filterValue) ? {filterValue} : {}),
      ...(filterType ? {filterType} : {}),
      ...(listQuantifier ? {listQuantifier} : {}),
      ...(listItemGroup ? {listItemGroup} : {}),
      ...(filterJoinType ? {filterJoinType} : {}),
      ...(profileHistoryOptions ? {profileHistoryOptions} : {}),
      ...(isNumber(startStepNumber) || startStepNumber === null ? {startStepNumber} : {}),
      ...(isNumber(endStepNumber) || endStepNumber === null ? {endStepNumber} : {}),
      ...(isHidden ? {isHidden} : {}),
      ...(isInherited ? {isInherited} : {}),
      ...(experiments ? {experiments} : {}),
    };
  }

  private eventToUrlData() {
    return {
      ...this.attrs,
      ...pick(this.attrs, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `dataGroupId`,
        `tempDataGroupId`,
      ]),
    };
  }

  toUrlData() {
    const conditionalAttrs: {
      customBucket?: Bucket;
      customGroups?: StringBucket;
      propertyType: PropertyType;
      typeCast?: PropertyType;
      unit?: TimeUnit;
      cohorts?: GroupByCohort[];
    } = {propertyType: this.propertyType};
    if (this.isDatetimeProperty) {
      conditionalAttrs.unit = this.unit || TIME_UNITS.DAY;
    }
    if (this.typeCast) {
      conditionalAttrs.typeCast = this.typeCast;
    }
    if (this.customBucket) {
      conditionalAttrs.customBucket = this.customBucket;
    }
    if (this.customGroups) {
      conditionalAttrs.customGroups = this.customGroups;
    }
    if (this.cohorts) {
      conditionalAttrs.cohorts = this.cohorts.map(this.cohortAttributes);
    }
    return {...this.eventToUrlData(), ...conditionalAttrs};
  }

  serialize() {
    return this.attrs;
  }

  trackingData() {
    return this.serialize();
  }

  toBookmarkData() {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  get isDatetimeProperty() {
    const realType = this.typeCast || this.propertyType;
    return realType === `datetime`;
  }
}

export function isPeopleGroupClause(groupClause: GroupClauseType) {
  return groupClause.resourceType === InsightsResourceType.People;
}

export function isOtherGroupClause(groupClause: GroupClauseType) {
  return groupClause.resourceType === InsightsResourceType.Other;
}

export function isEventGroupClause(groupClause: GroupClauseType) {
  return groupClause.resourceType === InsightsResourceType.Events;
}

export function isNumericGroupClause(groupClause: GroupClauseType) {
  if (isEventGroupClause(groupClause) || isPeopleGroupClause(groupClause)) {
    return groupClause.propertyType === PropertyType.Number;
  }
  return false;
}

export function isDateGroupClause(groupClause: GroupClauseType) {
  if (isEventGroupClause(groupClause) || isPeopleGroupClause(groupClause)) {
    return groupClause.propertyType === PropertyType.Datetime;
  }
  return false;
}

export function isCohortGroupClause(groupClause: GroupClauseType) {
  return Array.isArray(groupClause.cohorts);
}

export function isTTCGroupClause(
  groupClause: GroupClauseType,
): groupClause is GroupClauseType & {startStepNumber: number} {
  return groupClause.value === FUNNELS_TIME_TO_CONVERSION.value;
}

export function isBehaviorGroupClause(
  groupClause: GroupClause,
): groupClause is GroupClause & {behavior: GroupClauseType[`behavior`]} {
  return !!groupClause.behavior;
}
